import React, { useState, useEffect, useCallback } from 'react';
import * as XLSX from 'xlsx';
import api from '../services/api';
import { ListFooter } from '../ui';
import PartyTypeahead from './PartyTypeahead';
import { useAuth } from '../context/AuthContext';
import { reportError } from '../utils/reportError';

// Finance · Cash & Bank Day Book + Budget tracking (Part 1).
// Multi-tenant: a company switcher drives the X-Company-Id header on every request.

const inr = (v) => '₹' + (Number(v) || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });
const curMonth = () => new Date().toISOString().slice(0, 7);
const monthStart = (m) => m + '-01';
const monthEnd = (m) => { const [y, mo] = m.split('-').map(Number); return `${y}-${String(mo).padStart(2, '0')}-${new Date(y, mo, 0).getDate()}`; };
const inputS = { width: '100%', padding: '7px 9px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' };
const labelS = { fontSize: 10, color: '#888', fontWeight: 700, display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 };
const btn = (bg, c = '#fff') => ({ padding: '7px 14px', background: bg, color: c, border: 'none', borderRadius: 6, fontSize: 13, cursor: 'pointer', fontWeight: 600 });

// The Day Book filter bar's initial shape — ONE definition, shared by the useState initialiser
// and Clear's dirty check so the two can never drift. Dates are the CURRENT month, not blank:
// a blank `from` leaves the opening position unestablished and blanks the running-balance
// columns, which is a worse resting state than the default view.
// `amount` is declared here rather than springing into existence on the first keystroke. The
// `|| ''` guards at each read site stay — they still earn their keep on a cleared field.
// `verification` seeds from ?verification= so the pending-work tile lands on the rows it
// counted rather than on the whole month's register — the same discipline as the OE lists.
const initialFilt = () => {
  let v = '';
  try { v = new URLSearchParams(window.location.search).get('verification') || ''; } catch { v = ''; }
  // ARRIVING ON A VERIFICATION FILTER CLEARS THE DATE WINDOW, and it must. The default range
  // is the CURRENT month, but 7 of the 20 outstanding entries are from July — so a tile
  // reading 20 would open a register showing 13, which is the count-does-not-match-the-list
  // failure this whole tile exists to end. Blanking `from` normally costs the opening position
  // and the running-balance columns, but those are ALREADY disabled under a verification
  // filter (see balanceValid below), so nothing is lost that the view still had.
  return v
    ? { from: '', to: '', head: '', mode: '', verification: v, amount: '' }
    : { from: monthStart(curMonth()), to: monthEnd(curMonth()), head: '', mode: '', verification: '', amount: '' };
};

// Verification badge (mig 176). 'legacy' = predates the workflow — deliberately NOT shown as
// "unverified", so HR's outstanding queue means work actually waiting for them.
const VERIF = {
  verified:   { label: '✓ Verified',  bg: '#EAF7EE', fg: '#166534' },
  unverified: { label: 'Unverified',  bg: '#FEF3C7', fg: '#B45309' },
  legacy:     { label: 'Pre-workflow', bg: '#F1F1F4', fg: '#777' },
};

// ─── Amount search (client-side: GET /finance/daybook/entries has no LIMIT/OFFSET) ──
// RANGE, NOT EXACT, and deliberately so. People remember "3955" for a ₹3,955.50 payment, and
// an exact match would find nothing while looking like it worked. So a WHOLE-RUPEE entry
// matches its rupee band [n, n+1): typing 3955 finds 3955.00 and 3955.50 alike. Type the
// paise (3955.50) and it matches exactly, for when you do know them.
//
// On today's data the two rules are indistinguishable — all 338 entries are whole rupees —
// so this costs nothing now and stops being wrong the first time someone enters paise.
// MODULE LEVEL so its identity is stable across renders.
export const amountMatches = (amount, typed) => {
  const q = String(typed || '').trim().replace(/[₹,\s]/g, '');
  if (!q) return true;
  const n = Number(q);
  if (!Number.isFinite(n)) return false;
  const a = Math.abs(Number(amount));          // the sign is direction, not part of the number
  const v = Math.abs(n);
  return q.includes('.') ? Math.abs(a - v) < 0.005 : (a >= v && a < v + 1);
};

export default function CashBankDaybook() {
  const { user } = useAuth();
  // Roles that may CREATE/EDIT entries. hr reaches this screen only to verify + read totals,
  // so the write affordances are hidden for them (the backend enforces the same split).
  const canWrite = ['admin', 'management', 'accounts'].includes(user?.role);
  const [companies, setCompanies] = useState([]);
  const [canSwitch, setCanSwitch] = useState(false);
  const [company, setCompany] = useState('');
  // daybook | ledger | budget | reconcile | floats | heads — honour a ?sub= deep-link (the recon reminder links here).
  const [sub, setSub] = useState(() => {
    try { return new URLSearchParams(window.location.search).get('sub') || 'daybook'; } catch { return 'daybook'; }
  });

  // per-request company header
  const H = useCallback(() => ({ headers: { 'X-Company-Id': company } }), [company]);

  useEffect(() => {
    api.get('/finance/daybook/companies').then(r => {
      setCompanies(r.data.data || []); setCanSwitch(r.data.can_switch);
      setCompany(r.data.active || (r.data.data[0] && r.data.data[0].id) || '');
    }).catch(() => {});
  }, []);

  // hr sees only the register (to verify) and the totals — not budgets, floats, the expense-head
  // master or the reconciliation, none of which it has any part in.
  const subs = canWrite
    ? [['daybook', '🧾 Day Book'], ['ledger', '📒 Ledger'], ['totals', '📊 Totals'], ['budget', '🎯 Budget vs Actual'], ['reconcile', '⚖ Reconciliation'], ['floats', '💰 Floats & Balances'], ['heads', '⚙ Expense Heads']]
    : [['daybook', '🧾 Day Book'], ['totals', '📊 Totals']];

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#1a1a2e' }}>Cash & Bank Day Book</div>
          <div style={{ fontSize: 11, color: '#888' }}>Daily cash/bank expenses · linked to monthly budgets per head</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 10, color: '#888', fontWeight: 700, textTransform: 'uppercase' }}>Company</span>
          <select value={company} disabled={!canSwitch} onChange={e => setCompany(e.target.value)}
            style={{ ...inputS, width: 'auto', fontWeight: 700, color: '#3B3A8F', background: canSwitch ? '#EEEDF8' : '#f5f5f5', cursor: canSwitch ? 'pointer' : 'default' }}>
            {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid #eee', marginBottom: 14 }}>
        {subs.map(([k, l]) => (
          <button key={k} onClick={() => setSub(k)} style={{ padding: '9px 14px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 13, fontWeight: sub === k ? 700 : 400, color: sub === k ? '#3B3A8F' : '#888', borderBottom: sub === k ? '2px solid #3B3A8F' : '2px solid transparent' }}>{l}</button>
        ))}
      </div>

      {company && sub === 'daybook' && <DayBookTab company={company} H={H} canWrite={canWrite} />}
      {company && sub === 'ledger' && <LedgerTab H={H} />}
      {company && sub === 'totals' && <TotalsTab company={company} H={H} />}
      {company && sub === 'budget' && <BudgetTab company={company} H={H} />}
      {company && sub === 'reconcile' && <ReconcileTab company={company} H={H} />}
      {company && sub === 'floats' && <FloatsTab company={company} H={H} />}
      {company && sub === 'heads' && <HeadsTab company={company} H={H} />}
    </div>
  );
}

// ── Sub-tab: Day Book register ───────────────────────────────────────────────
// Every recorded change to one line (mig 401): who, when, each field before → after. Read-only.
// Collapsed until asked for, so the edit form stays the form.
function EntryHistory({ id, H }) {
  const [rows, setRows] = useState(null);
  const [failed, setFailed] = useState('');
  const open = () => api.get(`/finance/daybook/entries/${id}/history`, H())
    .then(r => setRows(r.data.data || [])).catch(e => setFailed(e.response?.data?.error || 'Could not load the history'));
  if (rows === null) return (
    <div style={{ marginBottom: 10, fontSize: 11 }}>
      <button type="button" onClick={open} style={{ background: 'none', border: 'none', color: '#3B3A8F', cursor: 'pointer', padding: 0, fontWeight: 700 }}>Show this line's history</button>
      {failed && <span style={{ color: '#A32D2D', marginLeft: 8 }}>{failed}</span>}
    </div>);
  const show = (v) => v === null || v === undefined || v === '' ? '—' : String(v);
  return (
    <div style={{ marginBottom: 12, fontSize: 11, background: '#FAFAFC', border: '1px solid #eee', borderRadius: 6, padding: '8px 10px' }}>
      <div style={{ fontWeight: 700, color: '#3B3A8F', marginBottom: 4 }}>History</div>
      {!rows.length && <div style={{ color: '#888' }}>No change recorded. Recording began on the day it was switched on; earlier edits left no trace.</div>}
      {rows.map((h, k) => (
        <div key={k} style={{ padding: '3px 0', borderTop: k ? '1px solid #eee' : 'none' }}>
          <strong>{h.action}</strong> · {new Date(h.changed_at).toLocaleString('en-IN')} · {h.by}
          {h.fields.map(f => <div key={f.field} style={{ color: '#555', marginLeft: 10 }}>{f.field}: {show(f.before)} → {show(f.after)}</div>)}
        </div>))}
    </div>);
}

function DayBookTab({ company, H, canWrite }) {
  const [entries, setEntries] = useState([]);
  const [heads, setHeads] = useState([]);
  const [gpos, setGpos] = useState([]);       // company-scoped GPOs for order-related picker
  const [gpoQuery, setGpoQuery] = useState(''); // searchable GPO picker text
  const [filt, setFilt] = useState(initialFilt);   // lazy initialiser — evaluated once, on mount
  // Outstanding-verification counts + whether THIS user may verify (mig 176). Company-wide and
  // date-unbounded — "waiting for HR" is not a property of the window being viewed.
  const [verif, setVerif] = useState({ unverified: 0, verified: 0, legacy: 0, can_verify: false });
  // The SERVER's today, used to cap the date picker and pre-validate. Never the browser's:
  // a machine with a wrong clock must not be able to book a future entry, nor be blocked from
  // a legitimate one. Null until the first load lands — the field stays uncapped for that
  // instant, and the backend rejects a future date regardless.
  const [serverDate, setServerDate] = useState(null);
  const [modal, setModal] = useState(null);   // entry being added/edited
  const [custodians, setCustodians] = useState([]);   // custodian typeahead results (id + full_name)
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [opening, setOpening] = useState({ established: false, cash: null, bank: null });  // carry-in position (before `from`)
  const [pos, setPos] = useState(null);        // live position + reconciliation meta (for the banner)

  const load = useCallback(() => {
    const p = new URLSearchParams();
    if (filt.from) p.set('from', filt.from); if (filt.to) p.set('to', filt.to);
    if (filt.head) p.set('head', filt.head); if (filt.mode) p.set('mode', filt.mode);
    if (filt.verification) p.set('verification', filt.verification);
    api.get('/finance/daybook/entries?' + p.toString(), H()).then(r => {
      setEntries(r.data.data || []); setOpening(r.data.opening || { established: false });
      if (r.data.verification) setVerif(r.data.verification);
      if (r.data.server_date) setServerDate(r.data.server_date);
    }).catch(() => {});
  }, [filt, H]);
  const loadPos = useCallback(() => { api.get('/finance/daybook/position', H()).then(r => setPos(r.data.data)).catch(() => setPos(null)); }, [H]);
  useEffect(() => { api.get('/finance/daybook/heads?active=1', H()).then(r => setHeads(r.data.data || [])).catch(() => {}); }, [H]);
  useEffect(() => { api.get('/finance/daybook/gpos', H()).then(r => setGpos(r.data.data || [])).catch(() => {}); }, [H]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadPos(); }, [loadPos]);

  // Signed running balances per mode (IN adds, OUT subtracts), seeded from the true carried-forward
  // balance, so the column reads as a real balance rather than a from-zero movement.
  // opening (design §A1/§B) — NOT reset by the date filter. When the window predates the opening
  // reconciliation, the position is "not established" and we show signed movement from zero instead.
  const established = !!opening.established;
  let cashRun = established ? Number(opening.cash) : 0;
  let bankRun = established ? Number(opening.bank) : 0;
  // THE AMOUNT FILTER APPLIES HERE, upstream of everything derived — so the window tiles
  // (Cash Paid, Bank Paid, Entries) and the table are computed from the SAME set and can
  // never disagree. Cash in Hand and Bank Balance are NOT derived from this: they come from
  // /finance/daybook/position and are a live POSITION, not a window figure — filtering them
  // would be claiming the tin holds ₹3,955 because that is what you searched for.
  const shown = entries.filter(e => amountMatches(e.amount, filt.amount));
  const rows = shown.map(e => {
    const signed = (e.direction === 'in' ? 1 : -1) * Number(e.amount);
    if (e.payment_mode === 'cash') cashRun += signed; else bankRun += signed;
    return { ...e, cashRun, bankRun };
  });
  // Period movement (paid out this window, by mode) — outflow only, for the SPEND stat.
  const paidCash = shown.filter(e => e.payment_mode === 'cash' && e.direction === 'out').reduce((s, e) => s + Number(e.amount), 0);
  const paidBank = shown.filter(e => e.payment_mode === 'bank' && e.direction === 'out').reduce((s, e) => s + Number(e.amount), 0);
  // A running balance is only meaningful when every entry in the range is shown — a head/mode
  // filter hides rows, so we blank the balance columns then (the balance is not "wrong", just
  // not computable from a filtered slice).
  const balanceValid = established && !filt.verification && !filt.head && !filt.mode && !String(filt.amount || '').trim();
  // Clear is only offered when something actually differs from the initial shape — on a fresh
  // load the button would be a no-op, so it isn't rendered at all. Compared key-by-key against
  // initialFilt() rather than by a stored snapshot, so it stays right if the shape gains a field.
  const filtDirty = Object.entries(initialFilt()).some(([k, v]) => String(filt[k] ?? '') !== String(v));

  const save = async (e) => {
    e.preventDefault(); setErr('');
    // Custodian must resolve to an employee — a typed name with no pick cannot be saved (item 1).
    if (showCustodian && (modal.custodian_name || '').trim() && !modal.custodian_id) {
      setErr('Pick the custodian from the employee list — a typed name that matches no employee cannot be saved.'); return;
    }
    // Description + Paid To required on new entries, and on any edit where the field already had a
    // value (item 2). Legacy blank rows are not retro-forced.
    // No future-dating. Compared against the SERVER's date, and only when the date is actually
    // being MOVED forward — an existing row that already carries a future date stays editable,
    // matching the backend exactly (no retro-block).
    if (serverDate && modal.entry_date > serverDate && modal.entry_date !== modal._origDate) {
      setErr('Entry date cannot be in the future — the Day Book records movements that have already happened.'); return;
    }
    // Stage 3 — the back-dated question. Asked only against an ASSERTED cut-off: against an
    // inferred one the answer would be written to a column no reader consults (ruling c),
    // and a question that changes nothing teaches people to click past questions.
    if (backDated && typeof modal.counted_in_last_count !== 'boolean') {
      setErr(`Say whether this money was already inside the physical count of ${fmtDate(pos.anchor_date)} — the book cannot otherwise tell a genuine later movement from one written down late.`); return;
    }
    const descReq = !modal.id || !!modal._descReq;
    if (descReq && !(modal.description || '').trim()) { setErr('Description / Narration is required.'); return; }
    // Received cash names its custodian — mirrors the server rule exactly. Cash only: a bank
    // receipt has no custodian, and the field is not offered for bank.
    if (isIn && modal.payment_mode === 'cash' && !modal.custodian_id) {
      setErr('Pick the custodian who took this cash into their float.'); return;
    }
    // Party (mig 176): a NEW entry must resolve to a payee_id. A legacy row that never had one
    // is not retro-blocked, but a row that already resolves to a party cannot be un-linked.
    // Both exempt inward — an inward float top-up has no payee and the field is not shown.
    if (!isIn && !modal.id && !modal.payee_id) { setErr('Paid To must be picked from the party list — use “Create and proceed” if the party is not there yet.'); return; }
    if (!isIn && modal.id && modal._hadPayee && !modal.payee_id) { setErr('Paid To cannot be cleared — pick a different party instead.'); return; }
    setSaving(true);
    try {
      const category = isIn ? 'general' : (modal.expense_category === 'order_related' ? 'order_related' : 'general');
      const body = { entry_date: modal.entry_date, payment_mode: modal.payment_mode,
        // An empty amount is SENT empty so the server refuses it in words; it is never turned into 0 (mig 401).
        description: (modal.description || '').trim(), amount: String(modal.amount ?? '').trim() === '' ? '' : Number(modal.amount),
        amount_to_confirm: !!modal.amount_to_confirm, voucher_no: modal.voucher_no,
        expense_category: category, gpo_id: category === 'order_related' ? (modal.gpo_id || null) : null,
        direction: modal.direction || 'out', custodian_id: modal.custodian_id || null, custodian_name: modal.custodian_name || null, transfer_mode: modal.transfer_mode || null,
        // Inward OMITS these two keys rather than sending null. PATCH guards on `'payee_id' in b`
        // and COALESCEs expense_head_id, so an explicit null would either 400 on the 19 existing
        // inward rows or blank a head that history still carries. Omitted = left exactly as-is.
        ...(isIn ? {} : { payee_id: modal.payee_id || null, expense_head_id: modal.expense_head_id || null }),
        ...(backDated ? { counted_in_last_count: modal.counted_in_last_count } : {}) };
      const send = (extra) => modal.id
        ? api.patch('/finance/daybook/entries/' + modal.id, { ...body, ...extra }, H())
        : api.post('/finance/daybook/entries', { ...body, ...extra }, H());
      try {
        await send({});
      } catch (dup) {
        // DUPLICATE WARNING (mig 188). A 409 carrying duplicate_warning is not a failure —
        // it is the server asking whether this really is a second payment. It names what it
        // matched (voucher, description, who keyed it, when) so the answer is informed, and
        // proceeds only on an explicit yes. Two genuine ₹100 payments in a day are possible.
        const d = dup.response?.data;
        if (dup.response?.status !== 409 || !d?.duplicate_warning) throw dup;
        const lines = (d.matches || []).map(m =>
          `  • ${m.voucher_no || 'no entry no.'} — ${m.description || '(no description)'}\n`
          + `    ${m.paid_to || '—'}${m.same_party ? '  ← SAME PARTY' : ''} · keyed by ${m.created_by_name || '—'} on ${String(m.created_at).slice(0, 10)}`
        ).join('\n');
        if (!window.confirm(
          `An entry already exists for this date, amount, direction and mode:\n\n${lines}\n\n`
          + `Save this as a SEPARATE payment anyway?`)) { setSaving(false); return; }
        await send({ confirm_duplicate: true });
      }
      setModal(null); load(); loadPos();
    } catch (e2) { setErr(e2.response?.data?.error || 'Failed to save'); } finally { setSaving(false); }
  };
  const del = async (id) => { if (!window.confirm('Delete this entry?')) return; await api.delete('/finance/daybook/entries/' + id, H()).catch(reportError('delete this entry')); load(); loadPos(); };

  // HR marks an entry checked. The balance does not move — it never depended on this.
  const verify = async (id) => {
    try { await api.post(`/finance/daybook/entries/${id}/verify`, {}, H()); load(); }
    catch (e2) { window.alert(e2.response?.data?.error || 'Could not verify this entry'); }
  };

  const exportXlsx = () => {
    const data = rows.map(r => ({
      Date: r.entry_date, Mode: r.payment_mode === 'cash' ? 'Cash' : 'Bank',
      Direction: r.direction === 'in' ? 'In' : 'Out', 'Expense Head': r.head_name || '—',
      Description: r.description || '', 'Paid To': r.payee_name || r.paid_to || '',
      Party: r.payee_id ? 'Linked' : 'Unlinked', Verification: (VERIF[r.verification_status] || {}).label || '',
      'Day Book Entry No': r.voucher_no || '',
      // Signed amount (+ received / − paid) so the balance columns reconcile.
      Amount: (r.direction === 'in' ? 1 : -1) * Number(r.amount),
      'Running Cash in Hand': established ? r.cashRun : '', 'Running Bank Balance': established ? r.bankRun : '',
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    ws['!cols'] = [{ wch: 12 }, { wch: 7 }, { wch: 8 }, { wch: 22 }, { wch: 26 }, { wch: 22 }, { wch: 9 }, { wch: 13 }, { wch: 12 }, { wch: 12 }, { wch: 17 }, { wch: 17 }];
    const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, ws, 'Day Book');
    XLSX.writeFile(wb, `khyati_daybook_${filt.from}_to_${filt.to}.xlsx`);
  };

  const th = { padding: '8px 10px', textAlign: 'left', color: '#555', fontWeight: 600, borderBottom: '2px solid #eee', fontSize: 11, whiteSpace: 'nowrap' };
  const td = { padding: '8px 10px', fontSize: 12, borderBottom: '1px solid #f5f5f5', whiteSpace: 'nowrap' };
  // Defaults for a money-IN row. Both are DEFAULTS, not constants — the narration is free text
  // and the custodian is re-pickable. The custodian is resolved by NAME against the employee
  // master rather than by a hardcoded id, so it follows the master if the float changes hands.
  const DEFAULT_IN_DESC = 'Cash in Chest';
  const DEFAULT_IN_CUSTODIAN = 'Diksha Kumari';
  // Switching direction. Money-IN drops the fields its form no longer carries (head, category,
  // GPO, party) and seeds the two a float top-up almost always wants.
  const pickDirection = (val) => {
    if (val !== 'in') { setModal(m => ({ ...m, direction: 'out' })); return; }
    setGpoQuery('');
    setModal(m => ({ ...m, direction: 'in',
      expense_category: 'general', gpo_id: null, expense_head_id: null, payee_id: null, payee_name: '',
      description: (m.description || '').trim() || DEFAULT_IN_DESC }));
    api.get('/finance/daybook/custodian-lookup?q=' + encodeURIComponent(DEFAULT_IN_CUSTODIAN), H())
      .then(r => {
        const list = r.data.data || [];
        const hit = list.find(x => x.full_name === DEFAULT_IN_CUSTODIAN);
        if (!hit) return;                       // no such employee — validation still asks
        setCustodians(list);
        // Only fills a BLANK custodian, so re-picking In↓ never overwrites a chosen name.
        setModal(m => (m.custodian_id ? m : { ...m, custodian_id: hit.id, custodian_name: hit.full_name }));
      })
      .catch(() => {});
  };

  // Advance heads (ADVANCE / Staff Advances) → custodian typeahead also shows for them.
  const advanceHeadIds = new Set(heads.filter(h => /advance/i.test(h.name || '')).map(h => h.id));
  // EVERY cash entry names its custodian, both directions. Gated to money-in, the OUT side of
  // a float was structurally unrecordable: daybook_custodian_balance computes Σin − Σout, but
  // Σout could only ever be zero, so net-held climbed forever (₹99,736 'held' against ₹1,311
  // actually in the drawer). Bank rows keep no custodian — the bank is in nobody's hands.
  const showCustodian = !!modal && (modal.payment_mode === 'cash' || advanceHeadIds.has(modal.expense_head_id));
  // MONEY IN. A float top-up is four facts — date, what it was, whose float it went into, how
  // much. It has no expense head (nothing was spent) and no payee (nobody was paid), so those
  // fields are not shown at all rather than shown and left blank.
  const isIn = !!modal && (modal.direction || 'out') === 'in';
  // Stage 3 — a NEW entry dated on or before an asserted count has to say whether its money
  // was already in the drawer. Creates only: the backend asks on POST and not on PATCH, and a
  // form that asks a question the server will not read is worse than one that does not ask.
  const backDated = !!modal && !modal.id && !!pos?.coverage_asserted && !!pos?.anchor_date
    && !!modal.entry_date && modal.entry_date <= pos.anchor_date;
  // Custodian stops being optional inward: without one the receipt never reaches Floats &
  // Balances. Cash only — a bank receipt is in nobody's hands and the field is not offered.
  const custodianRequired = isIn && modal.payment_mode === 'cash';
  // Description + Paid To required on new entries, and on edits where the field already had a value.
  const descRequired = !!modal && (!modal.id || !!modal._descReq);
  // Party is required on a NEW entry, and on an edit of a row that already resolves to one
  // (it may be re-pointed, never un-linked). A legacy row is not retro-forced to acquire one.
  // Never inward — that form has no Paid To field to satisfy.
  const partyRequired = !isIn && !!modal && (!modal.id || !!modal._hadPayee);

  return (
    <div>
      <PositionBanner pos={pos} />
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 12 }}>
        <div><label style={labelS}>From</label><input type="date" style={{ ...inputS, width: 140 }} value={filt.from} onChange={e => setFilt({ ...filt, from: e.target.value })} /></div>
        <div><label style={labelS}>To</label><input type="date" style={{ ...inputS, width: 140 }} value={filt.to} onChange={e => setFilt({ ...filt, to: e.target.value })} /></div>
        <div><label style={labelS}>Head</label><select style={{ ...inputS, width: 170 }} value={filt.head} onChange={e => setFilt({ ...filt, head: e.target.value })}><option value="">All heads</option>{heads.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}</select></div>
        <div><label style={labelS}>Mode</label><select style={{ ...inputS, width: 100 }} value={filt.mode} onChange={e => setFilt({ ...filt, mode: e.target.value })}><option value="">All</option><option value="cash">Cash</option><option value="bank">Bank</option></select></div>
        <div><label style={labelS}>Amount</label>
          <input style={{ ...inputS, width: 110 }} inputMode="decimal" placeholder="e.g. 3955"
            title='Finds an entry by its amount. A whole number matches its rupee band — 3955 finds ₹3,955.00 and ₹3,955.50.'
            value={filt.amount || ''} onChange={e => setFilt({ ...filt, amount: e.target.value })} /></div>
        <div><label style={labelS}>Verification</label>
          <select style={{ ...inputS, width: 140 }} value={filt.verification} onChange={e => setFilt({ ...filt, verification: e.target.value })}>
            <option value="">All</option>
            <option value="unverified">Unverified</option>
            <option value="verified">Verified</option>
            <option value="legacy">Pre-workflow</option>
          </select>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          {filtDirty && <button onClick={() => setFilt(initialFilt())} style={btn('#6B6B6B')}>✕ Clear</button>}
          <button onClick={exportXlsx} style={btn('#166534')}>⬇ Export</button>
          {/* New entries default to the SERVER's today (falling back to the browser's only
              before the first load lands) — so a skewed client clock doesn't pre-fill a date
              the server will then reject. */}
          {canWrite && <button onClick={() => { setErr(''); setGpoQuery(''); setCustodians([]); setModal({ entry_date: '', payment_mode: 'cash', amount: '', expense_category: 'general', direction: 'out' }); }} style={btn('#3B3A8F')}>+ Add Entry</button>}
        </div>
      </div>

      {/* Outstanding verification — a SOFT gate. This is a to-do list, never a warning about the
          numbers: every entry counted below is already in every balance and every total. */}
      {verif.unverified > 0 && (
        <div style={{ background: '#FEF3C7', border: '1px solid #FDE68A', color: '#8A5A00', padding: '9px 13px', borderRadius: 8, marginBottom: 12, fontSize: 12, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span><b>{verif.unverified}</b> {verif.unverified === 1 ? 'entry is' : 'entries are'} awaiting HR verification.</span>
          <span style={{ color: '#A98436' }}>They already count toward every balance and total — only the sign-off is outstanding.</span>
          {filt.verification !== 'unverified' && (
            <button onClick={() => setFilt({ ...filt, verification: 'unverified', from: '', to: '' })}
              style={{ ...btn('#B45309'), padding: '4px 10px', fontSize: 11, marginLeft: 'auto' }}>Show them</button>
          )}
        </div>
      )}

      {/* Stats strip — POSITION (live balances) kept strictly separate from SPEND (paid this window). */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
        <Stat label="Cash in Hand" value={pos && pos.established ? inr(pos.cash) : '—'} c="#854F0B" />
        <Stat label="Bank Balance" value={pos && pos.established ? inr(pos.bank) : '—'} c="#0E7490" />
        <Stat label="Cash Paid (window)" value={inr(paidCash)} c="#9A6A00" />
        <Stat label="Bank Paid (window)" value={inr(paidBank)} c="#0891B2" />
        <Stat label="Entries" value={rows.length} c="#166534" />
      </div>

      <div style={{ overflowX: 'auto', border: '1px solid #E8E7E7', borderRadius: 8 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead><tr style={{ background: '#F7F6F3' }}>
            {['Date', 'Mode', 'Dir', 'Expense Head', 'GPO', 'Description', 'Paid To', 'Entry No', 'Amount', 'Cash in Hand', 'Bank Balance', 'Verification', ''].map(h => <th key={h} style={{ ...th, textAlign: ['Amount', 'Cash in Hand', 'Bank Balance'].includes(h) ? 'right' : 'left' }}>{h}</th>)}
          </tr></thead>
          <tbody>
            {rows.length === 0 ? <tr><td colSpan={13} style={{ padding: 24, textAlign: 'center', color: '#aaa' }}>
              {(() => {
                const active = [
                  String(filt.amount || '').trim() && `amount ${String(filt.amount).trim()}`,
                  filt.head && `head ${(heads.find(h => String(h.id) === String(filt.head)) || {}).name || filt.head}`,
                  filt.mode && `mode ${filt.mode}`,
                  filt.verification && `${filt.verification}`,
                  (filt.from || filt.to) && 'that date range',
                ].filter(Boolean);
                return active.length
                  ? `Nothing matched ${active.join(' + ')}${entries.length ? ` — ${entries.length} ${entries.length === 1 ? 'entry' : 'entries'} in the date range.` : '.'}`
                  : 'No entries yet.';
              })()}
            </td></tr>
            : rows.map(r => {
              const isIn = r.direction === 'in';
              return (
              <tr key={r.id}>
                <td style={td}>{new Date(r.entry_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</td>
                <td style={td}><span style={{ padding: '2px 8px', borderRadius: 8, fontSize: 10, fontWeight: 700, background: r.payment_mode === 'cash' ? '#FAEEDA' : '#E0F2FE', color: r.payment_mode === 'cash' ? '#854F0B' : '#0E7490' }}>{r.payment_mode === 'cash' ? 'Cash' : 'Bank'}</span></td>
                <td style={td}><span style={{ padding: '2px 7px', borderRadius: 8, fontSize: 10, fontWeight: 700, background: isIn ? '#EAF7EE' : '#FCEBEB', color: isIn ? '#166534' : '#A32D2D' }}>{isIn ? 'IN' : 'OUT'}</span></td>
                <td style={td}>{r.head_name || <span style={{ color: '#ccc' }}>—</span>}</td>
                <td style={td}>{r.gpo_no ? <span title={r.gpo_style_no || ''} style={{ padding: '2px 7px', borderRadius: 8, fontSize: 10, fontWeight: 700, background: '#EEEDF8', color: '#3B3A8F' }}>{r.gpo_no}</span> : <span style={{ color: '#ccc' }}>—</span>}</td>
                <td style={{ ...td, whiteSpace: 'normal', maxWidth: 220, color: '#555' }}>{r.description || '—'}</td>
                {/* payee_name = the linked party master record; paid_to is the legacy text a
                    pre-176 row still carries and is all we have for it. */}
                <td style={td}>{r.payee_name || r.paid_to || '—'}{!r.payee_id && (r.paid_to || '') !== '' && <span style={{ marginLeft: 6, fontSize: 9, color: '#B45309', fontWeight: 700 }}>unlinked</span>}</td>
                <td style={{ ...td, color: '#888' }}>{r.voucher_no || '—'}</td>
                <td style={{ ...td, textAlign: 'right', fontWeight: 700, color: isIn ? '#166534' : '#333' }}>{isIn ? '+' : '−'}{inr(r.amount)}
                  {r.amount_to_confirm && <div style={{ fontSize: 9, color: '#B45309', fontWeight: 700 }} title="Entered as a best figure; still to be confirmed">to be confirmed</div>}
                  {r.review_reason && <div style={{ fontSize: 9, color: '#A32D2D', fontWeight: 700, whiteSpace: 'normal' }}>⚠ {r.review_reason}</div>}
                </td>
                <td style={{ ...td, textAlign: 'right', color: '#854F0B' }}>{balanceValid ? inr(r.cashRun) : '—'}</td>
                <td style={{ ...td, textAlign: 'right', color: '#0E7490' }}>{balanceValid ? inr(r.bankRun) : '—'}</td>
                <td style={td}>
                  {(() => {
                    const v = VERIF[r.verification_status] || VERIF.unverified;
                    return <span title={r.verified_by_name ? `Verified by ${r.verified_by_name}` : ''}
                      style={{ padding: '2px 8px', borderRadius: 8, fontSize: 10, fontWeight: 700, background: v.bg, color: v.fg }}>{v.label}</span>;
                  })()}
                  {/* Verify is offered on anything not already verified — including a pre-workflow
                      row, so the legacy backlog can be worked down rather than being frozen. */}
                  {verif.can_verify && r.verification_status !== 'verified' && (
                    <button onClick={() => verify(r.id)} style={{ ...btn('#EAF7EE', '#166534'), padding: '3px 9px', fontSize: 11, marginLeft: 6 }}>Verify</button>
                  )}
                </td>
                <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                  {canWrite && <>
                    <button onClick={() => { setErr(''); setGpoQuery(r.gpo_no || ''); setModal({ ...r, amount: String(r.amount), expense_category: r.expense_category || 'general', _descReq: !!(r.description || '').trim(), _hadPayee: !!r.payee_id, _origDate: r.entry_date }); }} style={{ ...btn('#EEEDF8', '#3B3A8F'), padding: '3px 9px', fontSize: 11, marginRight: 5 }}>✎</button>
                    <button onClick={() => del(r.id)} style={{ ...btn('#FCEBEB', '#A32D2D'), padding: '3px 9px', fontSize: 11 }}>🗑</button>
                  </>}
                </td>
              </tr>
            );})}
          </tbody>
        </table>
      </div>
      <ListFooter shown={rows.length} total={rows.length} noun="entries" />

      {modal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', borderRadius: 12, padding: 26, width: 480 }}>
            <div style={{ background: '#2D2C72', margin: '-26px -26px 18px', padding: '14px 22px', borderRadius: '12px 12px 0 0' }}><h3 style={{ margin: 0, fontSize: 16, color: '#fff', fontWeight: 700 }}>{modal.id ? 'Edit Entry' : 'New Day Book Entry'}</h3></div>
            {err && <div style={{ background: '#FCEBEB', color: '#A32D2D', padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: 13 }}>{err}</div>}
            {modal.id && <EntryHistory id={modal.id} H={H} />}
            <form onSubmit={save}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div><label style={labelS}>Date *</label>
                  {/* max = the SERVER's today, so the future is not pickable. An existing row
                      already dated ahead keeps its own value reachable (max is widened to it)
                      rather than becoming uneditable — the no-retro-block rule. */}
                  <input type="date" required style={inputS} value={modal.entry_date}
                    max={serverDate ? (modal._origDate && modal._origDate > serverDate ? modal._origDate : serverDate) : undefined}
                    onChange={e => setModal({ ...modal, entry_date: e.target.value })} />
                  {serverDate && modal.entry_date > serverDate && modal.entry_date !== modal._origDate && (
                    <div style={{ fontSize: 10, color: '#A32D2D', marginTop: 3 }}>Cannot be in the future.</div>
                  )}
                  {backDated && (
                    <div style={{ marginTop: 6, background: '#FEF7F0', border: '1px solid #F0D9BE', borderRadius: 6, padding: '8px 10px' }}>
                      <div style={{ fontSize: 11, color: '#854F0B', fontWeight: 700, marginBottom: 5 }}>
                        Dated on or before the counted position of {fmtDate(pos.anchor_date)}
                      </div>
                      <div style={{ fontSize: 11, color: '#7a6a55', marginBottom: 6 }}>
                        Was this money already inside that physical count? If it was, it is not a new movement and must not be added again.
                      </div>
                      <div style={{ display: 'flex', gap: 6 }}>
                        {[[true, 'Yes — already counted'], [false, 'No — a later movement']].map(([val, lbl]) => {
                          const on = modal.counted_in_last_count === val;
                          return (
                            <button type="button" key={String(val)} onClick={() => setModal({ ...modal, counted_in_last_count: val })}
                              style={{ ...btn(on ? '#854F0B' : '#F7F6F3', on ? '#fff' : '#666'), padding: '4px 10px', fontSize: 11 }}>{lbl}</button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
                <div><label style={labelS}>Payment Mode *</label><select required style={inputS} value={modal.payment_mode} onChange={e => setModal({ ...modal, payment_mode: e.target.value })}><option value="cash">Cash</option><option value="bank">Bank</option></select></div>
                <div><label style={labelS}>Direction</label>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {[['out', 'Out ↑ paid'], ['in', 'In ↓ received']].map(([val, lbl]) => {
                      const on = (modal.direction || 'out') === val;
                      return <button type="button" key={val} onClick={() => pickDirection(val)}
                        style={{ flex: 1, padding: '8px', borderRadius: 6, border: '1px solid ' + (on ? '#3B3A8F' : '#ddd'), background: on ? '#EEEDF8' : '#fff', color: on ? '#3B3A8F' : '#666', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>{lbl}</button>;
                    })}
                  </div>
                </div>
                {modal.payment_mode === 'bank' && (
                  <div><label style={labelS}>Transfer Mode</label>
                    <select style={inputS} value={modal.transfer_mode || ''} onChange={e => setModal({ ...modal, transfer_mode: e.target.value || null })}>
                      <option value="">— select —</option>
                      {['cheque', 'upi', 'neft', 'rtgs'].map(m => <option key={m} value={m}>{m.toUpperCase()}</option>)}
                    </select>
                  </div>
                )}
                {/* Expense Type, GPO, Expense Head and Paid To are the OUT form. A float
                    top-up spends nothing and pays nobody, so they are absent inward rather
                    than present and blank. */}
                {!isIn && (
                <div style={{ gridColumn: '1/-1' }}>
                  <label style={labelS}>Expense Type *</label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {[['general', 'General / Overhead'], ['order_related', 'Order-related (GPO)']].map(([val, lbl]) => (
                      <button type="button" key={val}
                        onClick={() => { if (val === 'general') setGpoQuery(''); setModal({ ...modal, expense_category: val, ...(val === 'general' ? { gpo_id: null } : {}) }); }}
                        style={{ flex: 1, padding: '8px', borderRadius: 6, border: '1px solid ' + (modal.expense_category === val ? '#3B3A8F' : '#ddd'), background: modal.expense_category === val ? '#EEEDF8' : '#fff', color: modal.expense_category === val ? '#3B3A8F' : '#666', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>{lbl}</button>
                    ))}
                  </div>
                </div>)}
                {!isIn && modal.expense_category === 'order_related' && (
                  <div style={{ gridColumn: '1/-1' }}>
                    <label style={labelS}>GPO * <span style={{ textTransform: 'none', color: '#aaa', fontWeight: 400 }}>— the order this expense belongs to</span></label>
                    <input list="daybook-gpo-list" required placeholder="Type a GPO no to search…" style={inputS}
                      value={gpoQuery}
                      onChange={e => { const q = e.target.value; setGpoQuery(q); const g = gpos.find(x => x.gpo_no === q); setModal({ ...modal, gpo_id: g ? g.id : null }); }} />
                    <datalist id="daybook-gpo-list">{gpos.map(g => <option key={g.id} value={g.gpo_no}>{g.style_no || ''}</option>)}</datalist>
                    {gpos.length === 0
                      ? <div style={{ fontSize: 10, color: '#A32D2D', marginTop: 3 }}>No GPOs for this company yet.</div>
                      : (gpoQuery && !modal.gpo_id) && <div style={{ fontSize: 10, color: '#A32D2D', marginTop: 3 }}>Pick a GPO from the list.</div>}
                  </div>
                )}
                {!isIn && <div style={{ gridColumn: '1/-1' }}><label style={labelS}>Expense Head</label><select style={inputS} value={modal.expense_head_id || ''} onChange={e => setModal({ ...modal, expense_head_id: e.target.value })}><option value="">— select —</option>{heads.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}</select></div>}
                {showCustodian && (
                  <div style={{ gridColumn: '1/-1' }}>
                    <label style={labelS}>Custodian (employee){custodianRequired && <span style={{ color: '#E22D2D' }}> *</span>} <span style={{ textTransform: 'none', color: '#aaa', fontWeight: 400 }}>— {modal.direction === 'in' ? 'who received it into their float' : 'whose float this was paid from'}</span></label>
                    <input list="daybook-custodian-list" placeholder="Type an employee name…" style={inputS}
                      value={modal.custodian_name || ''}
                      onChange={e => { const q = e.target.value; api.get('/finance/daybook/custodian-lookup?q=' + encodeURIComponent(q), H()).then(r => setCustodians(r.data.data || [])).catch(() => {}); const m = (custodians || []).find(x => x.full_name === q); setModal({ ...modal, custodian_name: q, custodian_id: m ? m.id : null }); }} />
                    <datalist id="daybook-custodian-list">{custodians.map(c => <option key={c.id} value={c.full_name} />)}</datalist>
                    {modal.custodian_name && !modal.custodian_id && <div style={{ fontSize: 10, color: '#A32D2D', marginTop: 3 }}>Pick an employee from the list.</div>}
                  </div>
                )}
                <div style={{ gridColumn: '1/-1' }}><label style={labelS}>Description / Narration{descRequired && <span style={{ color: '#E22D2D' }}> *</span>}</label><input style={inputS} required={descRequired} value={modal.description || ''} onChange={e => setModal({ ...modal, description: e.target.value })} /></div>
                <div><label style={labelS}>Amount (₹) *</label><input type="number" required min="0.01" step="0.01" style={inputS} value={modal.amount} onChange={e => setModal({ ...modal, amount: e.target.value })} />
                  {/* THE HONEST PLACE FOR A FIGURE NOT YET KNOWN (mig 401): the best figure, marked provisional —
                      never Rs 0 or Rs 1 standing in for it. */}
                  <label style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 5, fontSize: 11, color: '#555' }}>
                    <input type="checkbox" checked={!!modal.amount_to_confirm} onChange={e => setModal({ ...modal, amount_to_confirm: e.target.checked })} />
                    Amount to be confirmed — this is my best figure, not the final one
                  </label>
                </div>
                {!isIn && (
                <div>
                  <label style={labelS}>Paid To{partyRequired && <span style={{ color: '#E22D2D' }}> *</span>} <span style={{ textTransform: 'none', color: '#aaa', fontWeight: 400 }}>— party master</span></label>
                  <PartyTypeahead
                    value={modal.payee_id || ''} initialLabel={modal.payee_name || modal.paid_to || ''}
                    headers={H()}
                    onChange={(id, p) => setModal(m => ({ ...m, payee_id: id || null, payee_name: p ? p.name : '' }))} />
                  {/* A legacy row keeps its text until someone picks a party for it — it is never
                      retro-blocked, and the text is what the unlinked total is built from. */}
                  {modal.id && !modal._hadPayee && (
                    <div style={{ fontSize: 10, color: '#888', marginTop: 3 }}>
                      Legacy entry — recorded as “{modal.paid_to || '—'}”. Linking a party is optional here.
                    </div>
                  )}
                </div>)}
                <div style={{ gridColumn: '1/-1' }}>{/* Approved wording, 3 Oct 2026: this numbers the Day Book ENTRY; it is not an accounting
                    voucher and nothing here posts to a ledger. Issued numbers (RSR-V-####) are left as they are. */}<label style={labelS}>Day Book entry no. (not an accounting voucher)</label><input style={inputS} placeholder="Leave blank for the next number (e.g. RSR-V-0001)" value={modal.voucher_no || ''} onChange={e => setModal({ ...modal, voucher_no: e.target.value })} /><div style={{ fontSize: 10, color: '#888', marginTop: 3 }}>Numbers this Day Book entry. Nothing here posts to a ledger.{!modal.id && ' Leave blank for the next number.'}</div></div>
              </div>
              <div style={{ display: 'flex', gap: 10, marginTop: 18, justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setModal(null)} style={btn('#f0f2f8', '#444')}>Cancel</button>
                <button type="submit" disabled={saving} style={btn('#3B3A8F')}>{saving ? 'Saving…' : 'Save Entry'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Sub-tab: Totals — head-wise and party-wise (mig 176) ─────────────────────
// Both views are SPEND, per the mig-174 rule: outflows only, Internal Transfer excluded.
// So these totals answer "what did we spend, on what / to whom" — they are deliberately not
// the same number as the cash/bank POSITION, and are never summed with it.
// ── Sub-tab: Ledger ──────────────────────────────────────────────────────────
// A plain book: one account, a date range, In/Out/Balance on every row.
//
// The register next door is a SEARCHABLE LIST — head, mode, amount and verification all hide
// rows, and its running-balance columns correctly blank out whenever one is on, because a
// balance computed from a filtered slice is not a balance. That is most of the time in
// practice. This view exists so there is always somewhere to read a real running balance.
// It therefore takes NO filters beyond the book and the dates. Resist adding one.
function LedgerTab({ H }) {
  const [book, setBook] = useState('cash');
  const [custodians, setCustodians] = useState([]);
  const [range, setRange] = useState({ from: monthStart(curMonth()), to: monthEnd(curMonth()) });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  // The live company position. Deliberately the SAME /finance/daybook/position that feeds the
  // register's "Cash in Hand" tile — not a sum of the rows below. Summing the ledger would
  // drift from the tile the moment a date bound or a book differed, and two figures for one
  // drawer is worse than no figure at all.
  const [pos, setPos] = useState(null);

  useEffect(() => {
    api.get('/finance/daybook/position', H()).then(r => setPos(r.data.data)).catch(() => setPos(null));
  }, [H]);

  // Same source the Floats tab reads, so the two can never list different custodians.
  useEffect(() => {
    api.get('/finance/daybook/custodian-balance', H()).then(r => setCustodians(r.data.data || [])).catch(() => {});
  }, [H]);

  const load = useCallback(() => {
    setLoading(true);
    const q = new URLSearchParams({ book });
    if (range.from) q.set('from', range.from);
    if (range.to) q.set('to', range.to);
    api.get('/finance/daybook/ledger?' + q.toString(), H())
      .then(r => setData(r.data.data || null))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [book, range.from, range.to, H]);
  useEffect(() => { load(); }, [load]);

  const th = { padding: '8px 10px', textAlign: 'left', color: '#555', fontWeight: 600, borderBottom: '2px solid #eee', fontSize: 11, whiteSpace: 'nowrap' };
  const td = { padding: '8px 10px', fontSize: 12, borderBottom: '1px solid #f5f5f5', verticalAlign: 'top' };
  const num = { ...td, textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' };
  // 2 dp here, unlike the register's rounded display — a ledger that does not foot to the
  // paisa is not a ledger.
  const rs = (n) => n == null ? '' : Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const isCustodian = book.startsWith('custodian:');
  const rows = (data && data.rows) || [];
  const bookLabel = isCustodian
    ? ((custodians.find(c => 'custodian:' + c.employee_id === book) || {}).full_name || 'Custodian')
    : (book === 'bank' ? 'Bank' : 'Cash');

  const particulars = (r) => [r.voucher_no, r.payee_name, r.gpo_no ? 'GPO ' + r.gpo_no : null]
    .filter(Boolean).join(' · ');

  // Established mirrors the tile exactly: an em-dash, never a zero. A zero would assert the
  // drawer is empty when what we actually know is that nobody has counted it yet.
  const posOk = !!(pos && pos.established);
  // Total is cash + bank ONLY. A custodian float is money already sitting inside the cash
  // figure — Diksha's drawer IS part of the company's cash — so adding it would count the
  // same rupee twice and invent a pot that does not exist.
  const posTotal = posOk ? Number(pos.cash) + Number(pos.bank) : null;
  const seg = (label, value, active) => (
    <span style={{
      padding: '3px 10px', borderRadius: 6, fontSize: 12,
      background: active ? '#EEEDF8' : 'transparent',
      border: active ? '1px solid #C9C6EA' : '1px solid transparent',
      color: active ? '#3B3A8F' : '#555', fontWeight: active ? 700 : 500,
    }}>{label} <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{value}</strong></span>
  );

  const exportXlsx = () => {
    if (!data) return;
    const out = [{ Date: '', Particulars: `Opening balance${data.clamped && data.anchor_date ? ' (reconciliation of ' + data.anchor_date + ')' : ''}`, In: '', Out: '', Balance: Number(data.opening) }];
    rows.forEach(r => out.push({
      Date: r.entry_date,
      Particulars: [r.description, particulars(r)].filter(Boolean).join(' — '),
      In: r.direction === 'in' ? Number(r.amount) : '',
      Out: r.direction === 'out' ? Number(r.amount) : '',
      Balance: Number(r.balance),
    }));
    out.push({ Date: '', Particulars: 'Closing balance', In: '', Out: '', Balance: Number(data.closing) });
    const ws = XLSX.utils.json_to_sheet(out);
    ws['!cols'] = [{ wch: 12 }, { wch: 60 }, { wch: 13 }, { wch: 13 }, { wch: 14 }];
    const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, ws, 'Ledger');
    XLSX.writeFile(wb, `khyati_ledger_${bookLabel.replace(/\s+/g, '_')}_${range.from}_to_${range.to}.xlsx`);
  };

  return (
    <div>
      {/* Company position — always visible, whichever book is open, so the table below is
          always read against the whole. The open book is highlighted to tie the two together. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 12,
        padding: '8px 12px', background: '#FAFAFD', border: '1px solid #eee', borderRadius: 8 }}>
        <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: '#888', marginRight: 2 }}>Company position</span>
        {seg('Cash', posOk ? inr(pos.cash) : '—', book === 'cash')}
        <span style={{ color: '#ddd' }}>·</span>
        {seg('Bank', posOk ? inr(pos.bank) : '—', book === 'bank')}
        <span style={{ color: '#ddd' }}>·</span>
        {seg('Total', posOk ? inr(posTotal) : '—', false)}
        {!posOk && (
          <span style={{ fontSize: 11, color: '#8A5A00', marginLeft: 4 }}>
            not established — no counted opening yet, so there is no true position to show
          </span>
        )}
        {isCustodian && (
          <span style={{ fontSize: 11, color: '#888', marginLeft: 4 }}>
            The float below is <strong>part of</strong> the Cash figure, not additional to it.
          </span>
        )}
      </div>

      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 12 }}>
        <div>
          <label style={labelS}>Book</label>
          <select style={{ ...inputS, width: 220 }} value={book} onChange={e => setBook(e.target.value)}>
            <option value="cash">💵 Cash</option>
            <option value="bank">🏦 Bank</option>
            {custodians.length > 0 && (
              <optgroup label="Custodian floats">
                {custodians.map(c => <option key={c.employee_id} value={'custodian:' + c.employee_id}>{c.full_name}</option>)}
              </optgroup>
            )}
          </select>
        </div>
        <div><label style={labelS}>From</label><input type="date" style={{ ...inputS, width: 150 }} value={range.from} onChange={e => setRange({ ...range, from: e.target.value })} /></div>
        <div><label style={labelS}>To</label><input type="date" style={{ ...inputS, width: 150 }} value={range.to} onChange={e => setRange({ ...range, to: e.target.value })} /></div>
        <div style={{ marginLeft: 'auto' }}><button onClick={exportXlsx} disabled={!data} style={btn(data ? '#166534' : '#bbb')}>⬇ Export</button></div>
      </div>

      <div style={{ fontSize: 11, color: '#888', marginBottom: 10 }}>
        No head, mode, amount or verification filter here on purpose — every row carries a balance, so the book always foots.
      </div>

      {loading && <div style={{ padding: 20, textAlign: 'center', color: '#aaa', fontSize: 12 }}>Loading…</div>}

      {!loading && data && !data.anchor_date && (
        <div style={{ padding: 14, background: '#FFF8E6', border: '1px solid #F0E0B0', borderRadius: 8, fontSize: 12, color: '#8A5A00' }}>
          {data.note || 'No reconciliation anchor yet — a ledger has no opening balance until the first count.'}
        </div>
      )}

      {!loading && data && data.anchor_date && (
        <>
          {/* INFERRED CUT-OFF. This count carries no recorded cut-off time, so the ledger
              takes the moment it was SAVED as the boundary — a recorded fact, but not the
              same as knowing when the cash moved. The entries that inference swung are named
              so the reader can judge it rather than take it on trust.
              Amber, not red: nothing is hidden and nothing is wrong. Until the clamp landed
              these WERE hidden and this banner said so in red; saying that now, beside the
              rows it claims are missing, would be a false statement on the screen. */}
          {/* ASSERTED CUT-OFF (Stage 3). Once someone has stated what the count covers there
              is nothing to infer and nothing to explain — so this is a quiet statement of
              fact, in grey, not a banner. It is shown at all because the boundary is what
              decides which side of the count every row below sits on, and a reader checking
              a balance is entitled to see it without opening the Reconciliation tab. */}
          {data.coverage?.asserted && data.coverage.cutoff_at && (
            <div style={{ padding: '7px 12px', background: '#F7F6F3', borderRadius: 6, fontSize: 11.5, color: '#666', marginBottom: 10 }}>
              Opening counted on {fmtDate(data.anchor_date)}, covering everything recorded up to{' '}
              <strong>{new Date(data.coverage.cutoff_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</strong>.
              Rows below are movements after that moment.
            </div>
          )}

          {data.inferred_cutoff && (
            <div style={{ padding: '10px 12px', background: '#FBF7EF', border: '1px solid #f6d195', borderRadius: 6, fontSize: 12, color: '#9A6B00', marginBottom: 10 }}>
              <strong>This count has no recorded cut-off time.</strong> The ledger has taken the moment it was saved
              {data.inferred_cutoff.anchor_counted_at ? ' — ' + new Date(data.inferred_cutoff.anchor_counted_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) + ' on ' + fmtDate(data.anchor_date) : ''} — as the boundary.
              <div style={{ marginTop: 4 }}>
                {data.inferred_cutoff.count} {data.inferred_cutoff.count === 1 ? 'entry' : 'entries'} dated {fmtDate(data.anchor_date)} {data.inferred_cutoff.count === 1 ? 'was' : 'were'} posted after that moment
                and {data.inferred_cutoff.count === 1 ? 'is' : 'are'} therefore included below, worth <strong>{inr(Math.abs(data.inferred_cutoff.net))} {data.inferred_cutoff.net < 0 ? 'net paid out' : 'net received'}</strong>.
              </div>
              <div style={{ marginTop: 4, fontFamily: 'monospace', fontSize: 11 }}>{data.inferred_cutoff.vouchers.join(' · ')}</div>
              <div style={{ marginTop: 4, fontSize: 11 }}>
                If any of these was cash that had already left the drawer when it was counted, it is being counted twice — recording the cut-off on the count itself will settle it.
              </div>
            </div>
          )}
          {/* Backstop: physical cash cannot be negative. Shown only when the detector above
              found nothing, so the two never stack and contradict each other. */}
          {!data.inferred_cutoff && data.impossible_negative && (
            <div style={{ padding: '10px 12px', background: '#FCEBEB', border: '1px solid #F0C0C0', borderRadius: 6, fontSize: 12, color: '#A32D2D', marginBottom: 10 }}>
              <strong>This book closes negative ({inr(data.closing)}).</strong> Physical cash cannot go below zero, so something is being excluded or an entry is wrong. Check the Day Book for entries dated on or before {fmtDate(data.anchor_date)}.
            </div>
          )}
          {data.clamped && (
            <div style={{ padding: '8px 12px', background: '#EEEDF8', borderRadius: 6, fontSize: 11, color: '#3B3A8F', marginBottom: 10 }}>
              This ledger opens at the reconciliation of <strong>{fmtDate(data.anchor_date)}</strong> — entries before that date are already inside the counted balance and are not repeated here.
              {' '}<strong>Totals does not do this</strong>: it counts every entry in the month, including those dated {fmtDate(data.anchor_date)}, so the two tabs differ by design on the reconciliation date.
            </div>
          )}
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead><tr>
              <th style={th}>Date</th><th style={th}>Particulars</th>
              <th style={{ ...th, textAlign: 'right' }}>In (₹)</th>
              <th style={{ ...th, textAlign: 'right' }}>Out (₹)</th>
              <th style={{ ...th, textAlign: 'right' }}>Balance (₹)</th>
            </tr></thead>
            <tbody>
              <tr style={{ background: '#FAFAFD' }}>
                <td style={{ ...td, color: '#888' }}>{data.clamped && data.anchor_date ? fmtDate(data.anchor_date) : fmtDate(range.from)}</td>
                <td style={{ ...td, fontWeight: 700, color: '#555' }}>
                  Opening balance
                  {isCustodian && <span style={{ fontWeight: 400, color: '#888' }}> — a float is a running account, so it opens at zero</span>}
                </td>
                <td style={num}></td><td style={num}></td>
                <td style={{ ...num, fontWeight: 700 }}>{rs(data.opening)}</td>
              </tr>
              {rows.length === 0 && (
                <tr><td colSpan={5} style={{ ...td, textAlign: 'center', color: '#aaa', padding: 20 }}>No movements in this range.</td></tr>
              )}
              {rows.map(r => (
                <tr key={r.id}>
                  <td style={{ ...td, whiteSpace: 'nowrap', color: '#666' }}>{fmtDate(r.entry_date)}</td>
                  <td style={td}>
                    <div>{r.description || '—'}</div>
                    {particulars(r) && <div style={{ fontSize: 10, color: '#999', marginTop: 2 }}>{particulars(r)}</div>}
                  </td>
                  <td style={{ ...num, color: '#166534' }}>{r.direction === 'in' ? rs(r.amount) : ''}</td>
                  <td style={{ ...num, color: '#A32D2D' }}>{r.direction === 'out' ? rs(r.amount) : ''}</td>
                  <td style={{ ...num, fontWeight: 600, color: Number(r.balance) < 0 ? '#A32D2D' : '#1a1a2e' }}>{rs(r.balance)}</td>
                </tr>
              ))}
              <tr style={{ background: '#FAFAFD', borderTop: '2px solid #eee' }}>
                <td style={{ ...td, color: '#888' }}>{fmtDate(range.to)}</td>
                <td style={{ ...td, fontWeight: 700, color: '#555' }}>Closing balance</td>
                <td style={num}></td><td style={num}></td>
                <td style={{ ...num, fontWeight: 800, color: Number(data.closing) < 0 ? '#A32D2D' : '#166534' }}>{rs(data.closing)}</td>
              </tr>
            </tbody>
          </table>

          {/* The float's OUT side. Until the custodian field is filled on cash payments, money
              leaving a float is not attributable to it, so net-held reads high. Say so on the
              screen rather than presenting an understated balance as if it were complete. */}
          {isCustodian && data.unattributed && data.unattributed.count > 0 && (
            <div style={{ marginTop: 12, padding: '10px 12px', background: '#FCEBEB', border: '1px solid #F0C0C0', borderRadius: 8, fontSize: 12, color: '#A32D2D' }}>
              <strong>{data.unattributed.count} cash-out entr{data.unattributed.count === 1 ? 'y' : 'ies'} totalling ₹{rs(data.unattributed.total)}</strong> in this range carry no custodian, so this float is understated as a balance — money left the drawer without being charged to anyone's float.
            </div>
          )}
        </>
      )}
    </div>
  );
}

function TotalsTab({ company, H }) {
  const [view, setView] = useState('head');
  const [range, setRange] = useState({ from: monthStart(curMonth()), to: monthEnd(curMonth()) });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams({ from: range.from, to: range.to });
    api.get(`/finance/daybook/${view === 'head' ? 'head' : 'party'}-summary?` + p.toString(), H())
      .then(r => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [view, range, H]);
  useEffect(() => { load(); }, [load]);

  const rows = data?.data || [];
  const grand = Number(data?.totals?.total || 0);
  const th = { padding: '8px 10px', textAlign: 'left', color: '#555', fontWeight: 600, borderBottom: '2px solid #eee', fontSize: 11, whiteSpace: 'nowrap' };
  const td = { padding: '8px 10px', fontSize: 12, borderBottom: '1px solid #f5f5f5', whiteSpace: 'nowrap' };
  const rt = { ...td, textAlign: 'right' };

  const exportXlsx = () => {
    const out = rows.map(r => ({
      [view === 'head' ? 'Expense Head' : 'Party']: view === 'head' ? r.head_name : r.payee_name,
      Entries: r.entries, Cash: r.cash_total, Bank: r.bank_total, Total: r.total,
      'Share %': grand > 0 ? Math.round((r.total / grand) * 1000) / 10 : 0,
      'Unverified (₹)': r.unverified_total,
    }));
    const ws = XLSX.utils.json_to_sheet(out);
    ws['!cols'] = [{ wch: 30 }, { wch: 9 }, { wch: 13 }, { wch: 13 }, { wch: 14 }, { wch: 9 }, { wch: 14 }];
    const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, ws, view === 'head' ? 'Head-wise' : 'Party-wise');
    XLSX.writeFile(wb, `khyati_daybook_${view}_totals_${range.from}_to_${range.to}.xlsx`);
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 12 }}>
        <div style={{ display: 'flex', gap: 4 }}>
          {[['head', 'By Expense Head'], ['party', 'By Party']].map(([k, l]) => (
            <button key={k} onClick={() => setView(k)} style={{ padding: '8px 14px', borderRadius: 6, border: '1px solid ' + (view === k ? '#3B3A8F' : '#ddd'), background: view === k ? '#EEEDF8' : '#fff', color: view === k ? '#3B3A8F' : '#666', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>{l}</button>
          ))}
        </div>
        <div><label style={labelS}>From</label><input type="date" style={{ ...inputS, width: 140 }} value={range.from} onChange={e => setRange({ ...range, from: e.target.value })} /></div>
        <div><label style={labelS}>To</label><input type="date" style={{ ...inputS, width: 140 }} value={range.to} onChange={e => setRange({ ...range, to: e.target.value })} /></div>
        <div style={{ marginLeft: 'auto' }}><button onClick={exportXlsx} style={btn('#166534')}>⬇ Export</button></div>
      </div>

      <div style={{ fontSize: 11, color: '#888', marginBottom: 10 }}>
        Spend only — money paid OUT in this window. Internal Transfers (bank↔cash) are excluded, so
        moving money between the drawer and the bank never shows as spend. Unverified entries ARE included.
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
        <Stat label="Total Spend" value={inr(grand)} c="#A32D2D" />
        <Stat label="Cash" value={inr(data?.totals?.cash_total || 0)} c="#854F0B" />
        <Stat label="Bank" value={inr(data?.totals?.bank_total || 0)} c="#0E7490" />
        <Stat label={view === 'head' ? 'Heads' : 'Parties'} value={view === 'head' ? rows.length : (data?.party_count ?? 0)} c="#166534" />
        <Stat label="Entries" value={data?.totals?.entries || 0} c="#3B3A8F" />
      </div>

      {/* The Unassigned gap, stated plainly rather than folded into the table's noise. Rajan's
          ruling: legacy rows with no party show their count and value separately so the gap is
          VISIBLE. It shrinks only as entries are edited to pick a party — nothing back-fills it. */}
      {view === 'party' && (data?.unassigned?.entries > 0) && (
        <div style={{ background: '#F1F1F4', border: '1px solid #E2E2E8', color: '#555', padding: '9px 13px', borderRadius: 8, marginBottom: 12, fontSize: 12 }}>
          <b>{data.unassigned.entries}</b> {data.unassigned.entries === 1 ? 'entry' : 'entries'} worth <b>{inr(data.unassigned.total)}</b>
          {' '}({grand > 0 ? Math.round((data.unassigned.total / grand) * 100) : 0}% of the window) have no party linked — they predate the party master
          and carry only their original free text. They are counted in the total above, not dropped from it.
        </div>
      )}

      <div style={{ overflowX: 'auto', border: '1px solid #E8E7E7', borderRadius: 8 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead><tr style={{ background: '#F7F6F3' }}>
            {[view === 'head' ? 'Expense Head' : 'Party', 'Entries', 'Cash', 'Bank', 'Total', 'Share', 'Awaiting HR'].map(h => (
              <th key={h} style={{ ...th, textAlign: h === (view === 'head' ? 'Expense Head' : 'Party') ? 'left' : 'right' }}>{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={7} style={{ padding: 24, textAlign: 'center', color: '#aaa' }}>Loading…</td></tr>
            : rows.length === 0 ? <tr><td colSpan={7} style={{ padding: 24, textAlign: 'center', color: '#aaa' }}>No spend in this window</td></tr>
            : rows.map(r => {
              const unassigned = r.is_unassigned;
              return (
                <tr key={r.payee_id || r.expense_head_id || 'unassigned'} style={unassigned ? { background: '#FAFAFC' } : undefined}>
                  <td style={{ ...td, fontWeight: unassigned ? 700 : 400, color: unassigned ? '#777' : '#333' }}>
                    {view === 'head' ? r.head_name : r.payee_name}
                  </td>
                  <td style={rt}>{r.entries}</td>
                  <td style={{ ...rt, color: '#854F0B' }}>{r.cash_total ? inr(r.cash_total) : '—'}</td>
                  <td style={{ ...rt, color: '#0E7490' }}>{r.bank_total ? inr(r.bank_total) : '—'}</td>
                  <td style={{ ...rt, fontWeight: 700 }}>{inr(r.total)}</td>
                  <td style={{ ...rt, color: '#888' }}>{grand > 0 ? (Math.round((r.total / grand) * 1000) / 10) + '%' : '—'}</td>
                  <td style={{ ...rt, color: r.unverified_total > 0 ? '#B45309' : '#ccc' }}>
                    {r.unverified_total > 0 ? `${inr(r.unverified_total)} (${r.unverified_entries})` : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
          {rows.length > 0 && (
            <tfoot><tr style={{ background: '#F7F6F3', fontWeight: 700 }}>
              <td style={{ ...td, borderTop: '2px solid #eee' }}>Total</td>
              <td style={{ ...rt, borderTop: '2px solid #eee' }}>{data?.totals?.entries || 0}</td>
              <td style={{ ...rt, borderTop: '2px solid #eee', color: '#854F0B' }}>{inr(data?.totals?.cash_total || 0)}</td>
              <td style={{ ...rt, borderTop: '2px solid #eee', color: '#0E7490' }}>{inr(data?.totals?.bank_total || 0)}</td>
              <td style={{ ...rt, borderTop: '2px solid #eee' }}>{inr(grand)}</td>
              <td style={{ ...rt, borderTop: '2px solid #eee' }} colSpan={2} />
            </tr></tfoot>
          )}
        </table>
      </div>
      <ListFooter shown={rows.length} total={rows.length} noun={view === 'head' ? 'heads' : 'parties'} />
    </div>
  );
}

// ── Sub-tab: Budget vs Actual (also sets budgets) ────────────────────────────
// Floats & Balances — custodian float/advance balances from the daybook_custodian_balance view.
// net_held = Σin − Σout (Rajan-ruled). id + full_name only (no PII).
function FloatsTab({ H }) {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => api.get('/finance/daybook/custodian-balance', H()).then(r => setRows(r.data.data || [])).catch(() => {}), [H]);
  useEffect(() => { load(); }, [load]);
  const th = { padding: '8px 10px', textAlign: 'left', color: '#555', fontWeight: 600, borderBottom: '2px solid #eee', fontSize: 11, whiteSpace: 'nowrap' };
  const td = { padding: '8px 10px', fontSize: 12, borderBottom: '1px solid #f5f5f5', whiteSpace: 'nowrap' };
  const inr = (n) => '₹' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return (
    <div>
      <div style={{ fontSize: 12, color: '#888', marginBottom: 10 }}>Custodian floats &amp; advances — <strong>net held = money received IN minus paid OUT</strong> per employee.</div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead><tr>{['Employee', 'Net Held (₹)', 'Entries', 'Last Movement'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
        <tbody>
          {rows.length === 0 ? <tr><td colSpan={4} style={{ ...td, textAlign: 'center', color: '#aaa', padding: 20 }}>No custodian floats yet.</td></tr>
            : rows.map(r => (
              <tr key={r.employee_id}>
                <td style={{ ...td, fontWeight: 600 }}>{r.full_name}</td>
                <td style={{ ...td, fontWeight: 700, color: Number(r.net_held) < 0 ? '#A32D2D' : '#166534' }}>{inr(r.net_held)}</td>
                <td style={td}>{r.entries}</td>
                <td style={{ ...td, color: '#888' }}>{r.last_movement ? new Date(r.last_movement).toLocaleDateString('en-IN') : '—'}</td>
              </tr>
            ))}
        </tbody>
      </table>
      <div style={{ fontSize: 10, color: '#aaa', marginTop: 10, fontStyle: 'italic' }}>Note: the Day Book's cash/bank running totals do not yet net by direction — this Floats view tracks custodian in/out only (v1).</div>
    </div>
  );
}

function BudgetTab({ company, H }) {
  const [month, setMonth] = useState(curMonth());
  const [data, setData] = useState({ data: [], totals: { budget: 0, actual: 0, remaining: 0, pct_used: 0 } });
  const [edit, setEdit] = useState({});   // head_id -> draft amount

  const load = useCallback(() => {
    api.get('/finance/daybook/budget-vs-actual?month=' + month, H()).then(r => setData(r.data)).catch(() => {});
  }, [month, H]);
  useEffect(() => { load(); }, [load]);

  const saveBudget = async (headId) => {
    const amount = Number(edit[headId]);
    await api.put('/finance/daybook/budgets', { expense_head_id: headId, month, amount: isNaN(amount) ? 0 : amount }, H()).catch(reportError('save this budget'));
    setEdit(p => { const n = { ...p }; delete n[headId]; return n; });
    load();
  };

  const t = data.totals;
  const th = { padding: '9px 12px', textAlign: 'left', color: '#555', fontWeight: 600, borderBottom: '2px solid #eee', fontSize: 11 };
  const td = { padding: '9px 12px', fontSize: 13, borderBottom: '1px solid #f5f5f5' };

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 14 }}>
        <label style={{ ...labelS, marginBottom: 0 }}>Month</label>
        <input type="month" style={{ ...inputS, width: 160 }} value={month} onChange={e => setMonth(e.target.value)} />
      </div>

      {/* Month summary */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <Stat label="Total Budgeted" value={inr(t.budget)} c="#3B3A8F" />
        <Stat label="Total Actual" value={inr(t.actual)} c={t.actual > t.budget ? '#A32D2D' : '#166534'} />
        <Stat label="Remaining" value={inr(t.remaining)} c={t.remaining < 0 ? '#A32D2D' : '#166534'} />
        <Stat label="% Used" value={(t.pct_used == null ? '—' : t.pct_used + '%')} c={t.pct_used > 100 ? '#A32D2D' : '#854F0B'} />
      </div>

      <div style={{ border: '1px solid #E8E7E7', borderRadius: 8, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead><tr style={{ background: '#F7F6F3' }}>
            {['Expense Head', 'Budget (₹)', 'Actual (₹)', 'Remaining', '% Used', 'Status'].map(h => <th key={h} style={{ ...th, textAlign: h === 'Expense Head' || h === 'Status' ? 'left' : 'right' }}>{h}</th>)}
          </tr></thead>
          <tbody>
            {data.data.map(r => {
              const over = r.over_budget;
              const drafting = edit[r.expense_head_id] !== undefined;
              return (
                <tr key={r.expense_head_id} style={{ background: over ? '#FFF5F5' : 'transparent' }}>
                  <td style={{ ...td, fontWeight: 600 }}>{r.name}</td>
                  <td style={{ ...td, textAlign: 'right' }}>
                    <input type="number" min="0" style={{ ...inputS, width: 110, textAlign: 'right', padding: '5px 8px' }}
                      value={drafting ? edit[r.expense_head_id] : r.budget}
                      onChange={e => setEdit(p => ({ ...p, [r.expense_head_id]: e.target.value }))}
                      onBlur={() => drafting && saveBudget(r.expense_head_id)}
                      onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }} />
                  </td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 700, color: over ? '#A32D2D' : '#333' }}>{inr(r.actual)}</td>
                  <td style={{ ...td, textAlign: 'right', color: r.remaining < 0 ? '#A32D2D' : '#166534', fontWeight: 600 }}>{inr(r.remaining)}</td>
                  <td style={{ ...td, textAlign: 'right' }}>
                    {r.pct_used == null ? <span style={{ color: '#A32D2D', fontWeight: 700 }}>no budget</span> : (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ width: 50, height: 6, background: '#eee', borderRadius: 4, overflow: 'hidden' }}><span style={{ display: 'block', height: '100%', width: Math.min(100, r.pct_used) + '%', background: over ? '#E22D2D' : r.pct_used > 80 ? '#E0A800' : '#22c55e' }} /></span>
                        <b style={{ color: over ? '#A32D2D' : '#555', fontSize: 12 }}>{r.pct_used}%</b>
                      </span>
                    )}
                  </td>
                  <td style={td}>{over ? <span style={{ padding: '2px 9px', borderRadius: 8, fontSize: 11, fontWeight: 700, background: '#FCEBEB', color: '#A32D2D' }}>🔴 Over budget</span> : r.actual > 0 ? <span style={{ padding: '2px 9px', borderRadius: 8, fontSize: 11, fontWeight: 700, background: '#EAF7EE', color: '#166534' }}>On track</span> : <span style={{ color: '#bbb', fontSize: 11 }}>—</span>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div style={{ fontSize: 11, color: '#999', marginTop: 8 }}>Tip: edit a Budget cell and press Enter (or click away) to save. Actual = sum of day-book entries for that head this month.</div>
    </div>
  );
}

// ── Sub-tab: Expense Heads master ────────────────────────────────────────────
function HeadsTab({ company, H }) {
  const [heads, setHeads] = useState([]);
  const [name, setName] = useState('');
  const [err, setErr] = useState('');
  const load = useCallback(() => api.get('/finance/daybook/heads', H()).then(r => setHeads(r.data.data || [])).catch(() => {}), [H]);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault(); setErr('');
    try { await api.post('/finance/daybook/heads', { name }, H()); setName(''); load(); }
    catch (e2) { setErr(e2.response?.data?.error || 'Failed'); }
  };
  const rename = async (h) => { const n = window.prompt('Rename head:', h.name); if (n && n.trim() && n !== h.name) { await api.patch('/finance/daybook/heads/' + h.id, { name: n.trim() }, H()).catch(reportError('rename this expense head')); load(); } };
  const toggle = async (h) => { await api.patch('/finance/daybook/heads/' + h.id, { is_active: !h.is_active }, H()).catch(reportError('change this expense head')); load(); };

  const th = { padding: '9px 12px', textAlign: 'left', color: '#555', fontWeight: 600, borderBottom: '2px solid #eee', fontSize: 11 };
  const td = { padding: '9px 12px', fontSize: 13, borderBottom: '1px solid #f5f5f5' };

  return (
    <div>
      <form onSubmit={add} style={{ display: 'flex', gap: 8, marginBottom: 14, alignItems: 'flex-end' }}>
        <div><label style={labelS}>New Expense Head</label><input style={{ ...inputS, width: 260 }} placeholder="e.g. Bank Charges" value={name} onChange={e => setName(e.target.value)} required /></div>
        <button type="submit" style={btn('#3B3A8F')}>+ Add Head</button>
        {err && <span style={{ color: '#A32D2D', fontSize: 12 }}>{err}</span>}
      </form>
      <div style={{ border: '1px solid #E8E7E7', borderRadius: 8, overflow: 'hidden', maxWidth: 560 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead><tr style={{ background: '#F7F6F3' }}>{['Expense Head', 'Status', 'Actions'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
          <tbody>
            {heads.map(h => (
              <tr key={h.id} style={{ opacity: h.is_active ? 1 : 0.55 }}>
                <td style={{ ...td, fontWeight: 600 }}>{h.name}</td>
                <td style={td}><span style={{ padding: '2px 9px', borderRadius: 8, fontSize: 11, fontWeight: 700, background: h.is_active ? '#EAF7EE' : '#FCEBEB', color: h.is_active ? '#166534' : '#A32D2D' }}>{h.is_active ? 'Active' : 'Inactive'}</span></td>
                <td style={td}>
                  <button onClick={() => rename(h)} style={{ ...btn('#EEEDF8', '#3B3A8F'), padding: '3px 10px', fontSize: 11, marginRight: 6 }}>✎ Rename</button>
                  <button onClick={() => toggle(h)} style={{ ...btn(h.is_active ? '#FCEBEB' : '#EAF7EE', h.is_active ? '#A32D2D' : '#166534'), padding: '3px 10px', fontSize: 11 }}>{h.is_active ? 'Deactivate' : 'Activate'}</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ListFooter shown={heads.length} total={heads.length} noun="expense heads" />
    </div>
  );
}

// ── Position / reconciliation banner (shown on the Day Book + Reconciliation tabs) ──
// Amber = a weekly reconciliation is due (weeks-since). Red = a large variance stands on the
// last reconciliation (kept visible — never silently absorbed). Never blocks anything (ruling 6).
function PositionBanner({ pos }) {
  if (!pos) return null;
  const banners = [];
  const lr = pos.last_recon;
  if (!lr) {
    banners.push(['red', '⚑ No opening position recorded yet — go to the Reconciliation tab and enter a counted opening (cash + bank) to start the true balance.']);
  } else if (pos.weeks_since >= 1) {
    banners.push(['amber', `⏰ Weekly reconciliation due — last reconciled ${fmtDate(lr.recon_date)} (${pos.weeks_since} week${pos.weeks_since === 1 ? '' : 's'} ago). Count the drawer and confirm the bank on the Reconciliation tab.`]);
  }
  if (lr && lr.needs_explanation) {
    const vc = Number(lr.variance_cash), vb = Number(lr.variance_bank);
    banners.push(['red', `⚠ Last reconciliation (${fmtDate(lr.recon_date)}) had a large variance — cash ${vc >= 0 ? '+' : '−'}${inr(Math.abs(vc))}, bank ${vb >= 0 ? '+' : '−'}${inr(Math.abs(vb))}. Note: ${lr.note || '—'}`]);
  }
  if (!banners.length) return null;
  const palette = { amber: ['#FFF7E6', '#E0A800', '#7A5A00'], red: ['#FDECEC', '#f5b5b5', '#A32D2D'] };
  return (
    <div style={{ marginBottom: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
      {banners.map(([tone, text], i) => {
        const [bg, bd, fg] = palette[tone];
        return <div key={i} style={{ background: bg, border: `1px solid ${bd}`, color: fg, borderRadius: 8, padding: '9px 13px', fontSize: 12.5, fontWeight: 600 }}>{text}</div>;
      })}
    </div>
  );
}

// ── Sub-tab: Weekly reconciliation ───────────────────────────────────────────
// Record the counted cash/bank against the system's live position. Append-only (a record, not a
// correction). Note mandatory when |variance| ≥ MIN(₹5,000, 5%) per mode; never a hard block.
function ReconcileTab({ H }) {
  const [pos, setPos] = useState(null);
  const [hist, setHist] = useState([]);
  const [form, setForm] = useState({ recon_date: '', actual_cash: '', actual_bank: '', note: '' });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  // Stage 3 — the entries whose membership of this count is genuinely in question, and the
  // reconciler's answer. Ticked by default: the common case is that everything recorded so
  // far is in the drawer, and the reconciler's job is to name the exceptions.
  const [cands, setCands] = useState(null);
  const [ticked, setTicked] = useState(() => new Set());

  const load = useCallback(() => {
    api.get('/finance/daybook/position', H()).then(r => setPos(r.data.data)).catch(() => setPos(null));
    api.get('/finance/daybook/reconciliations', H()).then(r => setHist(r.data.data || [])).catch(() => {});
  }, [H]);
  useEffect(() => { load(); }, [load]);

  // Reload the candidate list whenever the date the count claims to cover changes.
  useEffect(() => {
    if (!form.recon_date) return;
    let live = true;
    api.get(`/finance/daybook/reconciliations/coverage-candidates?recon_date=${form.recon_date}`, H())
      .then(r => { if (!live) return; const rows = r.data.data?.rows || []; setCands(r.data.data); setTicked(new Set(rows.map(x => x.id))); })
      .catch(() => { if (live) { setCands(null); setTicked(new Set()); } });
    return () => { live = false; };
  }, [form.recon_date, H]);

  const isGenesis = pos && !pos.last_recon;
  const calcCash = pos && pos.established ? Number(pos.cash) : (isGenesis ? null : 0);
  const calcBank = pos && pos.established ? Number(pos.bank) : (isGenesis ? null : 0);
  const ac = form.actual_cash === '' ? null : Number(form.actual_cash);
  const ab = form.actual_bank === '' ? null : Number(form.actual_bank);
  const varCash = (!isGenesis && ac != null && calcCash != null) ? ac - calcCash : null;
  const varBank = (!isGenesis && ab != null && calcBank != null) ? ab - calcBank : null;
  const trips = (v, calc) => v != null && calc != null && Math.abs(v) >= Math.min(5000, 0.05 * Math.abs(calc));
  const needsNote = trips(varCash, calcCash) || trips(varBank, calcBank);
  const canWrite = pos && pos.can_reconcile;

  const save = async (e) => {
    e.preventDefault(); setErr(''); setSaving(true);
    try {
      // counted_entry_ids is sent even when empty — its PRESENCE is what records that the
      // cut-off was asserted rather than left to be inferred. Omitting it would silently
      // fall back to ruling (c).
      await api.post('/finance/daybook/reconciliations',
        { recon_date: form.recon_date, actual_cash: ac, actual_bank: ab, note: form.note || null,
          counted_entry_ids: [...ticked] }, H());
      setForm({ recon_date: '', actual_cash: '', actual_bank: '', note: '' });
      load();
    } catch (e2) { setErr(e2.response?.data?.error || 'Failed to save'); } finally { setSaving(false); }
  };

  const th = { padding: '8px 10px', textAlign: 'left', color: '#555', fontWeight: 600, borderBottom: '2px solid #eee', fontSize: 11, whiteSpace: 'nowrap' };
  const td = { padding: '8px 10px', fontSize: 12, borderBottom: '1px solid #f5f5f5', whiteSpace: 'nowrap' };
  const vCell = (v) => v == null ? '—' : <span style={{ color: Math.abs(Number(v)) < 0.005 ? '#166534' : (Number(v) < 0 ? '#A32D2D' : '#854F0B'), fontWeight: 700 }}>{Number(v) >= 0 ? '+' : '−'}{inr(Math.abs(Number(v)))}</span>;

  return (
    <div>
      <PositionBanner pos={pos} />
      <div style={{ fontSize: 12, color: '#888', marginBottom: 12 }}>Every Saturday, count the cash drawer and confirm the bank, then record both here. The variance from the system's calculated balance is stored as a permanent record — the next week runs from your <strong>actual</strong> count.</div>

      {canWrite ? (
        <form onSubmit={save} style={{ border: '1px solid #E8E7E7', borderRadius: 10, padding: 18, marginBottom: 20, maxWidth: 640 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#2D2C72', marginBottom: 12 }}>{isGenesis ? '① Opening (genesis) reconciliation — establishes the position' : 'Record this week’s reconciliation'}</div>
          {err && <div style={{ background: '#FCEBEB', color: '#A32D2D', padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: 13 }}>{err}</div>}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, alignItems: 'end' }}>
            <div><label style={labelS}>Date *</label><input type="date" required style={inputS} value={form.recon_date} onChange={e => setForm({ ...form, recon_date: e.target.value })} /></div>
            <div />
            <div />
            <div>
              <label style={labelS}>System says (Cash)</label>
              <div style={{ padding: '7px 9px', background: '#F7F6F3', borderRadius: 6, fontSize: 14, fontWeight: 700, color: '#854F0B' }}>{isGenesis ? '— (opening)' : inr(calcCash)}</div>
            </div>
            <div><label style={labelS}>Counted Cash *</label><input type="number" required step="0.01" style={inputS} value={form.actual_cash} onChange={e => setForm({ ...form, actual_cash: e.target.value })} /></div>
            <div><label style={labelS}>Variance (Cash)</label><div style={{ padding: '7px 0', fontSize: 14 }}>{isGenesis ? <span style={{ color: '#aaa' }}>n/a</span> : vCell(varCash)}</div></div>
            <div>
              <label style={labelS}>System says (Bank)</label>
              <div style={{ padding: '7px 9px', background: '#F7F6F3', borderRadius: 6, fontSize: 14, fontWeight: 700, color: '#0E7490' }}>{isGenesis ? '— (opening)' : inr(calcBank)}</div>
            </div>
            <div><label style={labelS}>Confirmed Bank *</label><input type="number" required step="0.01" style={inputS} value={form.actual_bank} onChange={e => setForm({ ...form, actual_bank: e.target.value })} /></div>
            <div><label style={labelS}>Variance (Bank)</label><div style={{ padding: '7px 0', fontSize: 14 }}>{isGenesis ? <span style={{ color: '#aaa' }}>n/a</span> : vCell(varBank)}</div></div>
          </div>
          {cands && cands.rows && cands.rows.length > 0 && (
            <div style={{ marginTop: 18, border: '1px solid #E8E7E7', borderRadius: 8, overflow: 'hidden' }}>
              <div style={{ background: '#F7F6F3', padding: '9px 12px', fontSize: 12, fontWeight: 700, color: '#2D2C72' }}>
                What does this count cover?
                <span style={{ fontWeight: 400, color: '#666' }}> — untick anything whose money was <em>not</em> in the drawer when you counted it.</span>
              </div>
              <div style={{ padding: '8px 12px', fontSize: 11.5, color: '#777', borderBottom: '1px solid #eee' }}>
                These {cands.rows.length} {cands.rows.length === 1 ? 'entry has' : 'entries have'} been recorded since the last counted position
                ({fmtDate(cands.previous_anchor?.date)}). Everything older is already inside that count and is not asked about again.
              </div>
              <div style={{ maxHeight: 240, overflowY: 'auto' }}>
                {(() => { let run = 0; return cands.rows.map(c => {
                  const on = ticked.has(c.id);
                  // The running subtotal walks EVERY row, ticked or not — it is what the
                  // drawer should hold if the whole list is inside the count, and the number
                  // the counter compares against. Ticked-only net is in the footer.
                  run += (c.direction === 'in' ? 1 : -1) * Number(c.amount);
                  return (
                    <label key={c.id} style={{ display: 'flex', gap: 10, alignItems: 'baseline', padding: '7px 12px', borderBottom: '1px solid #f5f5f5',
                                               fontSize: 12, cursor: 'pointer', background: on ? '#fff' : '#FEF7F0' }}>
                      <input type="checkbox" checked={on} onChange={() => {
                        const n = new Set(ticked); if (on) n.delete(c.id); else n.add(c.id); setTicked(n);
                      }} />
                      <span style={{ fontFamily: 'monospace', color: '#888', minWidth: 92 }}>{c.voucher_no || '—'}</span>
                      <span style={{ minWidth: 74 }}>{fmtDate(c.entry_date)}</span>
                      <span style={{ color: c.direction === 'in' ? '#166534' : '#A32D2D', fontWeight: 700, minWidth: 88 }}>
                        {c.direction === 'in' ? '+' : '−'}{inr(Number(c.amount))}
                      </span>
                      <span style={{ color: '#555', flex: 1 }}>{c.description || c.payee_name || ''}</span>
                      <span style={{ color: '#999', fontSize: 11 }}>
                        recorded {new Date(c.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                      </span>
                      <span style={{ fontFamily: 'monospace', fontSize: 11, color: run < 0 ? '#A32D2D' : '#444', minWidth: 76, textAlign: 'right' }}>
                        {inr(run)}
                      </span>
                    </label>
                  );
                }); })()}
              </div>
              {(() => {
                const net = cands.rows.filter(c => ticked.has(c.id))
                  .reduce((t, c) => t + (c.direction === 'in' ? 1 : -1) * Number(c.amount), 0);
                return (
                  <div style={{ padding: '8px 12px', fontSize: 12, background: '#F7F6F3', borderTop: '1px solid #eee', display: 'flex', gap: 16 }}>
                    <span><strong>{ticked.size}</strong> of {cands.rows.length} ticked</span>
                    <span>net of ticked: <strong style={{ color: net < 0 ? '#A32D2D' : '#166534' }}>{net >= 0 ? '+' : '−'}{inr(Math.abs(net))}</strong></span>
                    <span style={{ color: '#888' }}>full list nets {inr(cands.rows.reduce((t, c) => t + (c.direction === 'in' ? 1 : -1) * Number(c.amount), 0))}</span>
                  </div>
                );
              })()}
              {ticked.size < cands.rows.length && (() => {
                // WHERE THE CUT-OFF ACTUALLY LANDS, AND WHAT THAT COSTS.
                //
                // covers_through is the longest ticked PREFIX, so the FIRST untick fixes the
                // boundary — and every ticked entry after it can no longer be covered by a
                // time and has to be stamped to this count one by one. Untick the last row and
                // that is nothing; untick the first and it is every other row on the list.
                //
                // That blast radius was invisible until now: the panel said how many were
                // unticked and nothing about what unticking THAT ONE did to the rest. A
                // control whose cost only becomes apparent after you commit is a trap, so the
                // cost is stated here, above the Record button, while it can still be undone.
                //
                // The server recomputes all of this in resolveCoverage and remains the only
                // authority on what is written. This mirrors the rule to SHOW it, and must be
                // changed with it.
                const firstOut = cands.rows.findIndex(c => !ticked.has(c.id));
                const boundary = firstOut > 0 ? cands.rows[firstOut - 1] : null;
                const named = cands.rows.slice(firstOut + 1).filter(c => ticked.has(c.id));
                const out = cands.rows.length - ticked.size;
                return (
                  <div style={{ padding: '8px 12px', fontSize: 11.5, color: '#854F0B', background: '#FEF7F0' }}>
                    <div>
                      {out} unticked — {out === 1 ? 'it stays' : 'they stay'} outside this count and {out === 1 ? 'remains' : 'remain'} visible in the ledger as a movement after it.
                    </div>
                    <div style={{ marginTop: 4 }}>
                      The cut-off therefore stops {boundary
                        ? <>at <strong>{boundary.voucher_no}</strong> ({new Date(boundary.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })})</>
                        : <>before the first entry on this list</>}.
                      {named.length > 0 && <> The <strong>{named.length}</strong> later {named.length === 1 ? 'entry' : 'entries'} you have left ticked
                        {named.length === 1 ? ' is' : ' are'} past that moment, so {named.length === 1 ? 'it' : 'each'} will be recorded against this count individually.
                        That is stored, not a problem — but it means this untick reaches {named.length} other {named.length === 1 ? 'row' : 'rows'}.</>}
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          <div style={{ marginTop: 14 }}>
            <label style={labelS}>Note {needsNote && <span style={{ color: '#A32D2D' }}>* required — large variance (≥ ₹5,000 or 5%)</span>}</label>
            <input style={{ ...inputS, ...(needsNote && !form.note ? { border: '1px solid #A32D2D' } : {}) }} placeholder={needsNote ? 'Explain the discrepancy before saving…' : 'Optional'} value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
            <button type="submit" disabled={saving || (needsNote && !form.note.trim())} style={{ ...btn('#3B3A8F'), opacity: (needsNote && !form.note.trim()) ? 0.5 : 1 }}>{saving ? 'Saving…' : (isGenesis ? 'Record Opening' : 'Record Reconciliation')}</button>
          </div>
        </form>
      ) : (
        <div style={{ background: '#F7F6F3', borderRadius: 8, padding: '12px 14px', marginBottom: 20, fontSize: 12.5, color: '#666' }}>Recording a reconciliation is limited to management. You can view the history below.</div>
      )}

      <div style={{ fontSize: 12, fontWeight: 700, color: '#555', marginBottom: 8 }}>History</div>
      <div style={{ overflowX: 'auto', border: '1px solid #E8E7E7', borderRadius: 8 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead><tr style={{ background: '#F7F6F3' }}>{['Date', 'Covers', 'Calc Cash', 'Counted Cash', 'Var Cash', 'Calc Bank', 'Confirmed Bank', 'Var Bank', 'By', 'Note'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
          <tbody>
            {hist.length === 0 ? <tr><td colSpan={10} style={{ ...td, textAlign: 'center', color: '#aaa', padding: 20 }}>No reconciliations yet.</td></tr>
              : hist.map(r => (
                <tr key={r.id} style={{ background: r.needs_explanation ? '#FFF7E6' : 'transparent' }}>
                  <td style={{ ...td, fontWeight: 600 }}>{fmtDate(r.recon_date)}{r.is_genesis && <span style={{ marginLeft: 6, fontSize: 9, fontWeight: 700, background: '#EEEDF8', color: '#3B3A8F', padding: '1px 6px', borderRadius: 6 }}>OPENING</span>}</td>
                  {/* Item 5 — whoever reconciles must be able to see which counts were ASKED
                      what they cover and which are being inferred for. An inferred row is not
                      wrong, but it is a weaker fact, and the person about to rely on it is
                      entitled to know that without reading the code. */}
                  <td style={td}>
                    {r.coverage_asserted
                      ? <span title="The counter stated this cut-off" style={{ fontSize: 10.5, color: '#166534' }}>
                          ✓ up to {new Date(r.covers_through).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                        </span>
                      : <span title="No cut-off was recorded; the book infers it from when the count was saved" style={{ fontSize: 10, fontWeight: 700, background: '#FBF7EF', color: '#9A6B00', padding: '1px 6px', borderRadius: 6 }}>
                          inferred
                        </span>}
                  </td>
                  <td style={td}>{r.calc_cash == null ? '—' : inr(r.calc_cash)}</td>
                  <td style={{ ...td, fontWeight: 700 }}>{inr(r.actual_cash)}</td>
                  <td style={td}>{vCell(r.variance_cash)}</td>
                  <td style={td}>{r.calc_bank == null ? '—' : inr(r.calc_bank)}</td>
                  <td style={{ ...td, fontWeight: 700 }}>{inr(r.actual_bank)}</td>
                  <td style={td}>{vCell(r.variance_bank)}</td>
                  <td style={{ ...td, color: '#888' }}>{r.reconciled_by_name || '—'}</td>
                  <td style={{ ...td, whiteSpace: 'normal', maxWidth: 240, color: '#555' }}>{r.note || '—'}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function fmtDate(d) { return d ? new Date(d + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'; }

function Stat({ label, value, c }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #E8E7E7', borderRadius: 8, padding: '10px 16px', minWidth: 120 }}>
      <div style={{ fontSize: 10, color: '#888', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 800, color: c, marginTop: 2 }}>{value}</div>
    </div>
  );
}
